"use client";
import type { GameData } from "@/app/games/(details)/types";
import { Action, ControlType } from "@/context/AnalyticsProvider";
import {
  GamePageSection,
  useGamePageAnalytics,
} from "@/context/AnalyticsProvider/gamePage";
import { cn } from "@/lib/utils";
import { isMobile } from "@/utils/browser";
import { PlayButton } from "@components/NewGamePage/TrailerPlayer/PlayButton";
import { Video as VideoIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { VideoModal } from "./VideoModal";

export interface VideoCardProps {
  gameData: GameData;
  className?: string;
}

export function TrailerPlayer({ gameData, className }: VideoCardProps) {
  const { track } = useGamePageAnalytics();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!videoRef.current) return;
    isModalOpen || isMobile()
      ? videoRef.current.pause()
      : videoRef.current.play();
  }, [isModalOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsModalOpen(open);

      if (open) {
        track({
          action: Action.PRESSED,
          extras: {
            control: "play-trailer",
            controlType: ControlType.BUTTON,
            section: GamePageSection.BENTO_BOXES,
          },
        });
      }
    },
    [track],
  );

  return (
    <>
      <div
        // biome-ignore lint/a11y/useSemanticElements: use div instead of button as we have child elements not supported by <button>
        role="button"
        tabIndex={0}
        onClick={() => handleOpenChange(true)}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && handleOpenChange(true)
        }
        aria-label="Play video"
        className={cn(
          "relative aspect-video glass-border glass-border-hover group cursor-pointer rounded-[1.25rem]",
          className,
        )}
      >
        <div className="absolute inset-0 overflow-hidden rounded-[1.25rem] bg-background/50">
          <video
            ref={videoRef}
            src={gameData.gameContent?.trailer_url}
            loop
            muted
            controls={false}
            playsInline
            preload="metadata"
            width="100%"
            height="100%"
            className="size-full object-cover"
          />
        </div>

        {/* Dim overlay */}
        <div className="absolute inset-0 size-full rounded-[1.25rem] pointer-events-none bg-darken-overlay" />

        {/* Title */}
        <div className="absolute left-[5%] top-[10%] text-neutral-50 font-suisse flex items-center gap-x-1.5 @4xl:flex-col @4xl:items-start">
          <VideoIcon className="@md:size-6 size-5" />
          <p className="@md:text-lg text-sm font-semibold">Official Trailer</p>
        </div>

        <PlayButton />
      </div>

      <VideoModal
        isModalOpen={isModalOpen}
        handleOpenChange={handleOpenChange}
        gameData={gameData}
      />
    </>
  );
}
